import { RefSelectProps, Select, SelectProps, Typography } from 'antd';
import { forwardRef, ForwardRefRenderFunction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { useLazyGetCustomersQuery } from '../../api/customers-api';
import styles from './customers-select.module.css';
import { useDebounceValue } from 'usehooks-ts';

interface Props extends Omit<SelectProps, 'filterOption' | 'showSearch' | 'onSearch' | 'onBlur'> {
  value?: string;
}

const CustomersSelect: ForwardRefRenderFunction<RefSelectProps, Props> = ({ value, ...props }, ref): JSX.Element => {
  const [getCustomers, { data: customers, isFetching: isCustomersFetching }] = useLazyGetCustomersQuery();
  const [customerSearchValue, setCustomerSearchValue] = useState('');
  const [debouncedValue] = useDebounceValue(customerSearchValue, 400);

  const { t } = useTranslation('customers');

  const handleSearch = (searchValue: string) => {
    setCustomerSearchValue(searchValue);
  };

  useEffect(() => {
    getCustomers({ page: 1, limit: 50, fullName: debouncedValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return (
    <>
      <Select
        ref={ref}
        allowClear={true}
        showSearch
        value={value || undefined}
        onSearch={handleSearch}
        filterOption={(inputValue, option) =>
          (option?.children ?? '').toString().toLowerCase().includes(inputValue.toLowerCase())
        }
        {...props}
        notFoundContent={isCustomersFetching ? <PageSpinner size="small" /> : null}
      >
        {customerSearchValue &&
          customers?.items.map(({ id, name, surname, email }) => (
            <Select.Option key={id} value={id}>
              {name} {surname} – <Typography.Text italic>{email}</Typography.Text>
            </Select.Option>
          ))}
      </Select>
      <Typography.Text type="secondary" className={styles.customersSelectHint}>
        {t('customersSelect.hint')}
      </Typography.Text>
    </>
  );
};

export default forwardRef(CustomersSelect);
